/**
 * Client-visible audit trail for `/admin`.
 *
 * Sentry Logs (`feature:admin`) stay the compliance backstop — they are
 * write-only from the browser, so this module dual-writes every event into:
 *   1. an in-memory ring buffer (live updates in the current tab)
 *   2. localStorage (survives refresh on this machine)
 *   3. an optional HTTP sink (POST `/admin/audit-events`) once Admin.tsx
 *      has credentials — 404/501 is expected until the backend ships
 *
 * Merge + session grouping live here so the tab and tests share one schema.
 */

import type { AdminAuditRecord, AdminAuditSource } from '@/lib/api/admin-types'

export const ADMIN_AUDIT_SENTRY_URL =
  'https://humblytics.sentry.io/explore/logs/?query=feature%3Aadmin'

const SESSION_KEY = 'humblytics:admin-session:v1'
const STORAGE_KEY = 'humblytics:admin-audit:v1'
const MAX_RECORDS = 500

export type AdminAuditCategory =
  | 'all'
  | 'access'
  | 'lookup'
  | 'impersonation'
  | 'operations'
  | 'crm'

type AdminAuditSession = {
  sessionId: string
  actorLabel: string
  actorEmail: string | null
  startedAt: string
  endedAt: string | null
  events: AdminAuditRecord[]
  impersonationCount: number
}

const ADMIN_EVENT_LABELS: Record<string, string> = {
  portal_opened: 'Opened portal',
  portal_login_submitted: 'Logged in',
  access_denied: 'Access denied',
  portal_exited: 'Session ended',
  tab_viewed: 'Viewed tab',
  user_search_performed: 'Searched users',
  user_health_viewed: 'Opened user',
  impersonation_session_started: 'Impersonated',
  impersonation_link_created: 'Copied impersonation link',
  custom_site_added: 'Added site',
  subscription_fixed: 'Fixed subscription',
  reconciliation_run: 'Ran reconciliation',
  user_email_changed: 'Changed email',
  account_deletion_completed: 'Completed deletion',
  account_deletion_cancelled: 'Cancelled deletion',
  contact_created: 'Created contact',
  contact_updated: 'Updated contact',
  contact_deleted: 'Deleted contact',
  contact_comm_logged: 'Logged comm',
}

const CATEGORY_EVENTS: Record<Exclude<AdminAuditCategory, 'all'>, ReadonlySet<string>> = {
  access: new Set([
    'portal_opened',
    'portal_login_submitted',
    'access_denied',
    'portal_exited',
    'tab_viewed',
  ]),
  lookup: new Set(['user_search_performed', 'user_health_viewed']),
  impersonation: new Set(['impersonation_session_started', 'impersonation_link_created']),
  operations: new Set([
    'custom_site_added',
    'subscription_fixed',
    'reconciliation_run',
    'user_email_changed',
    'account_deletion_completed',
    'account_deletion_cancelled',
  ]),
  crm: new Set([
    'contact_created',
    'contact_updated',
    'contact_deleted',
    'contact_comm_logged',
  ]),
}

const SOURCE_RANK: Record<AdminAuditSource, number> = {
  session: 1,
  browser: 2,
  shared: 3,
}

type AuditSink = (record: AdminAuditRecord) => void

let memory: AdminAuditRecord[] = []
let sink: AuditSink | null = null
const listeners = new Set<(records: AdminAuditRecord[]) => void>()

const canUseDom = () => typeof window !== 'undefined'

const newId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `audit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

export const adminEventLabel = (event: string): string =>
  (ADMIN_EVENT_LABELS as Record<string, string>)[event] ?? event.replaceAll('_', ' ')

export const adminEventCategory = (event: string): Exclude<AdminAuditCategory, 'all'> | null => {
  for (const [category, events] of Object.entries(CATEGORY_EVENTS)) {
    if (events.has(event)) return category as Exclude<AdminAuditCategory, 'all'>
  }
  return null
}

export const getAdminAuditSessionId = (): string => {
  if (!canUseDom()) return 'session'
  try {
    const existing = sessionStorage.getItem(SESSION_KEY)
    if (existing) return existing
    const id = newId()
    sessionStorage.setItem(SESSION_KEY, id)
    return id
  } catch {
    return 'session'
  }
}

/**
 * Drop the current tab's session id so the next `getAdminAuditSessionId()`
 * mints a fresh one. Called after `portal_exited` so a return visit in the same
 * tab starts a new session instead of merging into the one that just ended.
 */
const rotateAdminAuditSessionId = (): void => {
  if (!canUseDom()) return
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // sessionStorage unavailable — the next getAdminAuditSessionId falls back.
  }
}

/**
 * Wipe the admin audit trail from this browser. The buffer holds staff
 * identity, customer emails, search queries and impersonation targets, so it
 * must not survive sign-out on a shared workstation — `AuthContext.logout`
 * calls this alongside dropping the auth cookie.
 */
export const clearAdminAudit = (): void => {
  memory = []
  if (canUseDom()) {
    try {
      localStorage.removeItem(STORAGE_KEY)
      sessionStorage.removeItem(SESSION_KEY)
    } catch {
      // Private mode / quota — the in-memory buffer is already cleared.
    }
  }
  notify()
}

export const configureAdminAuditSink = (next: AuditSink | null) => {
  sink = next
}

const notify = () => {
  const snapshot = memory.slice()
  for (const listener of listeners) listener(snapshot)
}

const persistBrowser = (records: AdminAuditRecord[]) => {
  if (!canUseDom()) return
  try {
    const forDisk = records.map((record) => ({
      ...record,
      source: 'browser' as const,
    }))
    localStorage.setItem(STORAGE_KEY, JSON.stringify(forDisk.slice(0, MAX_RECORDS)))
  } catch {
    // Quota or private mode — the in-memory buffer still updates the tab.
  }
}

export const readLocalAdminAudit = (): AdminAuditRecord[] => {
  if (!canUseDom()) return memory.slice()
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return memory.slice()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return memory.slice()
    const stored = parsed.filter(isAuditRecord)
    memory = mergeAuditRecords(stored, memory)
    return memory.slice()
  } catch {
    return memory.slice()
  }
}

export const subscribeAdminAudit = (listener: (records: AdminAuditRecord[]) => void) => {
  listeners.add(listener)
  listener(memory.slice())
  return () => {
    listeners.delete(listener)
  }
}

export const recordAdminAudit = (
  input: Omit<AdminAuditRecord, 'id' | 'source' | 'admin_session_id'> & {
    id?: string
    admin_session_id?: string
    source?: AdminAuditSource
  },
): AdminAuditRecord => {
  const record: AdminAuditRecord = {
    ...input,
    id: input.id ?? newId(),
    admin_session_id: input.admin_session_id ?? getAdminAuditSessionId(),
    source: input.source ?? 'session',
  }

  memory = mergeAuditRecords(memory, [record]).slice(0, MAX_RECORDS)
  persistBrowser(memory)
  notify()

  try {
    sink?.(record)
  } catch {
    // Fire-and-forget — a sink failure must never break the portal.
  }

  // The exit event belongs to the session that just ended; rotate afterwards so
  // a return visit in the same tab opens a fresh session.
  if (record.event === 'portal_exited') rotateAdminAuditSessionId()

  return record
}

export const isAuditRecord = (value: unknown): value is AdminAuditRecord => {
  if (!value || typeof value !== 'object') return false
  const record = value as Partial<AdminAuditRecord>
  return (
    typeof record.id === 'string' &&
    typeof record.event === 'string' &&
    typeof record.occurred_at === 'string' &&
    typeof record.admin_session_id === 'string'
  )
}

export const mergeAuditRecords = (
  ...batches: AdminAuditRecord[][]
): AdminAuditRecord[] => {
  const byId = new Map<string, AdminAuditRecord>()
  for (const batch of batches) {
    for (const record of batch) {
      const existing = byId.get(record.id)
      if (!existing) {
        byId.set(record.id, record)
        continue
      }
      const existingRank = SOURCE_RANK[existing.source ?? 'session']
      const nextRank = SOURCE_RANK[record.source ?? 'session']
      byId.set(record.id, nextRank >= existingRank ? { ...existing, ...record } : existing)
    }
  }
  return [...byId.values()].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at))
}

export const actorKey = (record: AdminAuditRecord): string =>
  record.admin_email || record.admin_username || record.admin_user_id || 'unknown'

export const actorLabel = (record: Pick<AdminAuditRecord, 'admin_email' | 'admin_username' | 'admin_name' | 'admin_user_id'>): string =>
  record.admin_email || record.admin_name || record.admin_username || record.admin_user_id || 'Unknown admin'

export const filterAuditRecords = (
  records: AdminAuditRecord[],
  opts: {
    from?: Date
    to?: Date
    category?: AdminAuditCategory
    actor?: string
  },
): AdminAuditRecord[] => {
  const fromMs = opts.from ? opts.from.getTime() : null
  const toMs = opts.to ? opts.to.getTime() : null
  const category = opts.category && opts.category !== 'all' ? opts.category : null
  const actor = opts.actor && opts.actor !== 'all' ? opts.actor : null

  return records.filter((record) => {
    const at = Date.parse(record.occurred_at)
    if (Number.isNaN(at)) return false
    if (fromMs != null && at < fromMs) return false
    if (toMs != null && at > toMs) return false
    if (category && adminEventCategory(record.event) !== category) return false
    if (actor && actorKey(record) !== actor) return false
    return true
  })
}

const bestActor = (events: AdminAuditRecord[]) => {
  const preferred =
    events.find((event) => event.event === 'portal_login_submitted') ??
    events.find((event) => event.admin_email || event.admin_username)
  return preferred ?? events[0]
}

export const groupAuditSessions = (records: AdminAuditRecord[]): AdminAuditSession[] => {
  const grouped = new Map<string, AdminAuditRecord[]>()
  for (const record of records) {
    const key = record.admin_session_id || record.id
    const bucket = grouped.get(key)
    if (bucket) bucket.push(record)
    else grouped.set(key, [record])
  }

  return [...grouped.entries()]
    .map(([sessionId, events]) => {
      const chronological = events.slice().sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
      const actor = bestActor(chronological)
      const ended = chronological.find((event) => event.event === 'portal_exited')
      return {
        sessionId,
        actorLabel: actor ? actorLabel(actor) : 'Unknown admin',
        actorEmail: actor?.admin_email ?? null,
        startedAt: chronological[0]?.occurred_at ?? '',
        endedAt: ended?.occurred_at ?? null,
        events: chronological.slice().reverse(),
        impersonationCount: chronological.filter((event) =>
          event.event.startsWith('impersonation_'),
        ).length,
      }
    })
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
}

export const auditDetail = (record: AdminAuditRecord): string | null => {
  const attrs = record.attributes ?? {}
  if (record.event === 'tab_viewed' && typeof attrs.admin_tab === 'string') {
    return attrs.admin_tab
  }
  if (record.event === 'user_search_performed' && typeof attrs.search_query === 'string') {
    const count = typeof attrs.result_count === 'number' ? ` · ${attrs.result_count} results` : ''
    return `${attrs.search_query}${count}`
  }
  if (record.target_email) return record.target_email
  if (typeof attrs.domain === 'string') return attrs.domain
  if (typeof attrs.contact_email === 'string') return attrs.contact_email
  if (typeof attrs.admin_action === 'string') {
    const status = typeof attrs.http_status === 'number' ? ` · HTTP ${attrs.http_status}` : ''
    return `${attrs.admin_action}${status}`
  }
  if (typeof attrs.link_ttl === 'string') return `TTL ${attrs.link_ttl}`
  return null
}

/** Test helper — not used by production code. */
export const resetAdminAuditForTests = () => {
  memory = []
  sink = null
  listeners.clear()
}
